(() => {
  const MilestoneUtils = {
    createElement(tag, className) {
      const element = document.createElement(tag);
      element.className = className;
      return element;
    },

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    isMobile() {
      return window.innerWidth <= 768;
    },

    isRTL() {
      return (
        document.documentElement.dir === "rtl" ||
        document.documentElement.getAttribute("dir") === "rtl"
      );
    },
  };

  class MilestoneTimeline {
    constructor(sectionId) {
      this.sectionId = sectionId;
      this.config = {
        shortTicksPerSegment: 24,
        sliderWidth: 60,
        mobileBreakpoint: 768,
        tickSpacing: 7,
      };
      this.state = {
        currentIndex: 0,
        isDragging: false,
        mobileExpanded: false,
      };
      this.elements = this.initElements();
      this.init();
    }

    initElements() {
      const prefix = this.sectionId;
      return {
        cardsContainer: document.getElementById(`milestone-cards-${prefix}`),
        timelineContainer: document.getElementById(`timeline-container-${prefix}`),
        timelineSlider: document.getElementById(`timeline-slider-${prefix}`),
        timelineTicks: document.getElementById(`timeline-ticks-${prefix}`),
        timelineLabels: document.getElementById(`timeline-labels-${prefix}`),
        milestoneCards: document.querySelectorAll(
          `#milestone-cards-${prefix} .milestones__card`
        ),
        mobileTimeline: document.getElementById(`mobile-timeline-${prefix}`),
        mobileMilestones: document.querySelectorAll(
          `#mobile-timeline-${prefix} .milestones__mobile-milestone`
        ),
        mobileTimelineTicks: document.getElementById(
          `mobile-timeline-ticks-${prefix}`
        ),
        mobileTimelineLabels: document.getElementById(
          `mobile-timeline-labels-${prefix}`
        ),
        mobileViewMoreBtn: document.getElementById(`mobile-view-more-${prefix}`),
      };
    }

    init() {
      if (!this.validateElements()) return;

      const maxIndex = this.elements.milestoneCards.length - 1;
      this.elements.timelineSlider?.setAttribute("aria-valuemax", String(maxIndex));

      this.generateTimeline();
      this.bindEvents();
      this.updateState(0);
      this.handleResponsive();
      this.initializeMobileState();

      if (MilestoneUtils.isMobile()) {
        setTimeout(() => this.generateMobileTicks(), 150);
      }
    }

    initializeMobileState() {
      this.updateMobileVisibility();
      this.updateViewMoreButton();
      this.updateTimelineClass();
    }

    updateTimelineClass() {
      const { mobileTimeline, mobileMilestones, mobileViewMoreBtn } =
        this.elements;
      if (!mobileTimeline || !mobileMilestones.length) return;

      const hasHiddenContent =
        mobileTimeline.querySelector(".mobile-content-hidden") !== null;

      if (hasHiddenContent) {
        mobileTimeline.classList.add("has-hidden-content");
        if (mobileMilestones[2]) {
          mobileMilestones[2].classList.add("no-bottom-margin");
        }
      } else {
        mobileTimeline.classList.remove("has-hidden-content");
        if (mobileMilestones[2]) {
          mobileMilestones[2].classList.remove("no-bottom-margin");
        }
      }

      if (mobileViewMoreBtn) {
        mobileTimeline.classList.add("has-view-more");
      } else {
        mobileTimeline.classList.remove("has-view-more");
      }

      const lastMilestone = mobileMilestones[mobileMilestones.length - 1];
      if (lastMilestone) {
        lastMilestone.classList.add("no-bottom-margin");
      }
    }

    validateElements() {
      const {
        cardsContainer,
        timelineContainer,
        timelineSlider,
        timelineTicks,
        timelineLabels,
        milestoneCards,
        mobileTimeline,
        mobileMilestones,
      } = this.elements;

      return !!(
        cardsContainer &&
        timelineContainer &&
        timelineSlider &&
        timelineTicks &&
        timelineLabels &&
        milestoneCards.length &&
        mobileTimeline &&
        mobileMilestones.length
      );
    }

    generateTimeline() {
      this.generateTicks();
      this.generateLabels();
      this.generateMobileLabels();

      if (MilestoneUtils.isMobile()) {
        this.generateMobileTicks();
      }
    }

    generateTicks() {
      const { timelineTicks, milestoneCards } = this.elements;
      timelineTicks.innerHTML = "";
      const { segmentWidth } = this.getTimelineMetrics();

      milestoneCards.forEach((_, i) => {
        if (i > 0) {
          const yearTick = this.createElement(
            "div",
            "milestones__timeline-tick milestones__timeline-tick--year"
          );
          yearTick.style.insetInlineStart = `${i * segmentWidth}px`;
          yearTick.style.insetInlineEnd = "auto";
          yearTick.dataset.index = i;
          timelineTicks.appendChild(yearTick);
        }

        const { shortTicksPerSegment } = this.config;
        for (let j = 1; j <= shortTicksPerSegment; j++) {
          const shortTick = this.createElement(
            "div",
            "milestones__timeline-tick milestones__timeline-tick--between"
          );
          const tickPosition =
            i * segmentWidth + (j * segmentWidth) / (shortTicksPerSegment + 1);
          shortTick.style.insetInlineStart = `${tickPosition}px`;
          shortTick.style.insetInlineEnd = "auto";
          timelineTicks.appendChild(shortTick);
        }
      });
    }

    generateLabels() {
      const { timelineLabels, milestoneCards } = this.elements;
      timelineLabels.innerHTML = "";
      const { segmentWidth } = this.getTimelineMetrics();

      milestoneCards.forEach((card, i) => {
        const label = this.createElement(
          "button",
          "milestones__timeline-label"
        );
        label.type = "button";
        label.dataset.index = i;
        label.textContent = card.querySelector(
          ".milestones__card-label"
        ).textContent;
        label.style.insetInlineStart = `${i * segmentWidth}px`;
        label.style.insetInlineEnd = "auto";
        label.style.width = `${segmentWidth}px`;
        timelineLabels.appendChild(label);
      });
    }

    generateMobileTicks() {
      const { mobileTimelineTicks, mobileMilestones } = this.elements;

      if (!mobileTimelineTicks || !mobileMilestones?.length) return;
      if (!MilestoneUtils.isMobile()) return;

      mobileTimelineTicks.innerHTML = "";
      mobileTimelineTicks.style.insetInlineStart = "0";
      mobileTimelineTicks.style.insetInlineEnd = "auto";

      if (this.state.mobileExpanded) {
        this.generateAllMobileTicks();
      } else {
        this.generateVisibleMobileTicks();
      }
    }

    generateVisibleMobileTicks() {
      const { mobileTimelineTicks, mobileMilestones } = this.elements;
      if (!mobileTimelineTicks || !mobileMilestones.length) return;

      const visibleMilestones = Array.from(mobileMilestones).slice(0, 3);
      visibleMilestones.forEach((milestone, i) => {
        this.appendMobileTickSegment(
          mobileTimelineTicks,
          milestone,
          visibleMilestones[i + 1],
          i,
          i < visibleMilestones.length - 1
        );
      });
    }

    generateAllMobileTicks() {
      const { mobileTimelineTicks, mobileMilestones } = this.elements;
      if (!mobileTimelineTicks || !mobileMilestones.length) return;

      mobileMilestones.forEach((milestone, i) => {
        this.appendMobileTickSegment(
          mobileTimelineTicks,
          milestone,
          mobileMilestones[i + 1],
          i,
          i < mobileMilestones.length - 1
        );
      });
    }

    appendMobileTickSegment(
      container,
      milestone,
      nextMilestone,
      index,
      hasNext
    ) {
      const yearTickOffset = this.getMobileYearOffset(
        milestone,
        container,
        index * 76 + 8
      );

      const yearTick = this.createElement(
        "div",
        "milestones__mobile-timeline-tick milestones__mobile-timeline-tick--year"
      );
      yearTick.style.top = `${yearTickOffset}px`;
      yearTick.style.insetInlineStart = "0";
      yearTick.style.insetInlineEnd = "auto";
      yearTick.dataset.index = index;
      container.appendChild(yearTick);

      let segmentHeight;

      if (hasNext && nextMilestone) {
        const nextYearOffset = this.getMobileYearOffset(
          nextMilestone,
          container,
          (index + 1) * 76 + 8
        );
        segmentHeight = nextYearOffset - yearTickOffset;
      } else {
        const descriptionElement = milestone.querySelector(
          ".milestones__mobile-description"
        );
        let endOffset = yearTickOffset + 40;

        if (descriptionElement) {
          try {
            const descRect = descriptionElement.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            endOffset = descRect.bottom - containerRect.top + 8;
          } catch (_e) {
            /* use default */
          }
        }

        segmentHeight = endOffset - yearTickOffset;
      }

      this.addShortTicks(container, yearTickOffset, segmentHeight);
    }

    getMobileYearOffset(milestone, container, fallback) {
      const yearElement = milestone.querySelector(
        ".milestones__mobile-year"
      );
      if (!yearElement) return fallback;

      try {
        const yearRect = yearElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        return yearRect.top - containerRect.top;
      } catch (_e) {
        return fallback;
      }
    }

    addShortTicks(container, startTop, segmentHeight) {
      const tickCount = Math.max(
        1,
        Math.floor(segmentHeight / this.config.tickSpacing) - 1
      );

      for (let j = 1; j <= tickCount; j++) {
        const shortTick = this.createElement(
          "div",
          "milestones__mobile-timeline-tick milestones__mobile-timeline-tick--between"
        );
        const tickPosition =
          startTop + (j * segmentHeight) / (tickCount + 1);
        shortTick.style.top = `${tickPosition}px`;
        shortTick.style.insetInlineStart = "0";
        shortTick.style.insetInlineEnd = "auto";
        container.appendChild(shortTick);
      }
    }

    generateMobileLabels() {
      const { mobileTimelineLabels } = this.elements;
      if (mobileTimelineLabels) {
        mobileTimelineLabels.innerHTML = "";
      }
    }

    createElement(tag, className) {
      return MilestoneUtils.createElement(tag, className);
    }

    getTimelineMetrics() {
      const { timelineTicks, milestoneCards } = this.elements;
      const containerWidth = timelineTicks.offsetWidth;
      const segmentWidth = containerWidth / milestoneCards.length;
      return { containerWidth, segmentWidth };
    }

    updateState(index, syncScroll = false) {
      if (index === this.state.currentIndex && !syncScroll) return;
      this.state.currentIndex = index;
      this.updateCards(index);
      this.updateLabels(index);
      this.updateTicks(index);
      this.updateSlider(index, syncScroll);
    }

    updateCards(index) {
      const { milestoneCards, mobileMilestones } = this.elements;

      milestoneCards.forEach((card, i) => {
        const isActive = i === index;
        card.classList.toggle("active", isActive);
        card.classList.toggle("faded", !isActive);
      });

      mobileMilestones.forEach((milestone, i) => {
        const isActive = i === index;
        milestone.classList.toggle("active", isActive);
        milestone.setAttribute("aria-selected", String(isActive));
        milestone.setAttribute("tabindex", isActive ? "0" : "-1");
      });
    }

    updateLabels(index) {
      const { timelineLabels } = this.elements;
      timelineLabels
        .querySelectorAll(".milestones__timeline-label")
        .forEach((label, i) => {
          label.classList.toggle("active", i === index);
        });
    }

    updateTicks(index) {
      const { timelineTicks, mobileTimelineTicks } = this.elements;

      timelineTicks
        .querySelectorAll(".milestones__timeline-tick--year")
        .forEach((tick) => {
          const tickIndex = parseInt(tick.dataset.index, 10);
          tick.classList.toggle(
            "milestones__timeline-tick--active",
            tickIndex === index
          );
        });

      if (mobileTimelineTicks) {
        mobileTimelineTicks
          .querySelectorAll(
            ".milestones__mobile-timeline-tick--year"
          )
          .forEach((tick) => {
            const tickIndex = parseInt(tick.dataset.index, 10);
            tick.classList.toggle(
              "milestones__mobile-timeline-tick--active",
              tickIndex === index
            );
          });
      }
    }

    updateSlider(index, syncScroll = false) {
      const { timelineSlider, milestoneCards } = this.elements;
      const { segmentWidth } = this.getTimelineMetrics();

      timelineSlider.style.insetInlineStart = `${index * segmentWidth}px`;
      timelineSlider.style.insetInlineEnd = "auto";
      timelineSlider.setAttribute("aria-valuenow", String(index));

      if (syncScroll) {
        const targetCard = milestoneCards[index];
        if (targetCard) {
          targetCard.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "start",
          });
        }
      }
    }

    bindEvents() {
      this.bindSliderEvents();
      this.bindCardEvents();
      this.bindLabelEvents();
      this.bindTickEvents();
      this.bindMobileViewMoreEvents();
      this.bindResizeEvent();
    }

    bindSliderEvents() {
      const { timelineSlider } = this.elements;
      const isRTL = MilestoneUtils.isRTL();

      const startDrag = (e) => {
        if (this.state.isDragging) return;
        this.state.isDragging = true;
        this.dragStartX =
          e.type === "mousedown" ? e.pageX : e.touches[0].pageX;
        const sliderRect = timelineSlider.getBoundingClientRect();
        const containerRect =
          this.elements.timelineContainer.getBoundingClientRect();
        this.initialSliderPosition = isRTL
          ? containerRect.right - sliderRect.right
          : sliderRect.left - containerRect.left;
        timelineSlider.classList.add("dragging");
        e.preventDefault();
      };

      const moveDrag = (e) => {
        if (!this.state.isDragging) return;
        const currentX =
          e.type === "mousemove" ? e.pageX : e.touches[0].pageX;
        const deltaX = isRTL
          ? this.dragStartX - currentX
          : currentX - this.dragStartX;
        const newPosition = this.initialSliderPosition + deltaX;
        const { containerWidth, segmentWidth } = this.getTimelineMetrics();
        const maxPosition = containerWidth - this.config.sliderWidth;
        const clampedPosition = Math.max(0, Math.min(maxPosition, newPosition));

        timelineSlider.style.insetInlineStart = `${clampedPosition}px`;
        timelineSlider.style.insetInlineEnd = "auto";

        const index = Math.round(clampedPosition / segmentWidth);
        const clampedIndex = Math.max(
          0,
          Math.min(this.elements.milestoneCards.length - 1, index)
        );

        if (clampedIndex !== this.state.currentIndex) {
          this.updateCards(clampedIndex);
          this.updateLabels(clampedIndex);
          this.updateTicks(clampedIndex);
          this.state.currentIndex = clampedIndex;

          const targetCard = this.elements.milestoneCards[clampedIndex];
          if (targetCard) {
            targetCard.scrollIntoView({
              behavior: "auto",
              block: "nearest",
              inline: "start",
            });
          }
        } else {
          this.updateCards(clampedIndex);
        }
      };

      const endDrag = () => {
        if (!this.state.isDragging) return;
        this.state.isDragging = false;
        timelineSlider.classList.remove("dragging");

        const { segmentWidth } = this.getTimelineMetrics();
        const sliderRect = timelineSlider.getBoundingClientRect();
        const containerRect =
          this.elements.timelineContainer.getBoundingClientRect();
        const sliderPosition = isRTL
          ? containerRect.right - sliderRect.right
          : sliderRect.left - containerRect.left;
        const index = Math.round(sliderPosition / segmentWidth);
        const clampedIndex = Math.max(
          0,
          Math.min(this.elements.milestoneCards.length - 1, index)
        );

        this.updateState(clampedIndex, true);
      };

      timelineSlider.addEventListener("mousedown", startDrag);
      document.addEventListener("mousemove", moveDrag);
      document.addEventListener("mouseup", endDrag);
      timelineSlider.addEventListener("touchstart", startDrag, {
        passive: false,
      });
      document.addEventListener("touchmove", moveDrag, { passive: false });
      document.addEventListener("touchend", endDrag);
    }

    bindCardEvents() {
      const { milestoneCards, mobileMilestones } = this.elements;

      milestoneCards.forEach((card, index) => {
        card.addEventListener("click", () => {
          this.updateState(index, true);
        });
      });

      mobileMilestones.forEach((milestone, index) => {
        milestone.addEventListener("click", () => {
          this.updateState(index, true);
        });

        milestone.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.updateState(index, true);
          } else if (
            e.key === "ArrowDown" &&
            index < mobileMilestones.length - 1
          ) {
            e.preventDefault();
            mobileMilestones[index + 1].focus();
          } else if (e.key === "ArrowUp" && index > 0) {
            e.preventDefault();
            mobileMilestones[index - 1].focus();
          }
        });
      });
    }

    bindLabelEvents() {
      const { timelineLabels } = this.elements;
      timelineLabels
        .querySelectorAll(".milestones__timeline-label")
        .forEach((label) => {
          const index = parseInt(label.dataset.index, 10);
          label.addEventListener("click", () => {
            this.updateState(index, true);
          });
        });
    }

    bindTickEvents() {
      const { timelineTicks } = this.elements;
      timelineTicks
        .querySelectorAll(".milestones__timeline-tick--year")
        .forEach((tick) => {
          const index = parseInt(tick.dataset.index, 10);
          tick.addEventListener("click", () => {
            this.updateState(index, true);
          });
        });
    }

    bindMobileViewMoreEvents() {
      const { mobileViewMoreBtn } = this.elements;
      if (!mobileViewMoreBtn) return;

      mobileViewMoreBtn.addEventListener("click", () => {
        this.toggleMobileExpansion();
      });
    }

    toggleMobileExpansion() {
      this.state.mobileExpanded = true;
      this.updateMobileVisibility();
      this.updateViewMoreButton();
      this.updateTimelineClass();

      if (MilestoneUtils.isMobile()) {
        setTimeout(() => this.generateMobileTicks(), 100);
      }
    }

    updateMobileVisibility() {
      const { mobileMilestones } = this.elements;
      mobileMilestones.forEach((milestone, index) => {
        if (index >= 3) {
          milestone.classList.toggle(
            "mobile-content-hidden",
            !this.state.mobileExpanded
          );
          milestone.classList.toggle(
            "mobile-content-visible",
            this.state.mobileExpanded
          );
        }
      });
    }

    updateViewMoreButton() {
      const { mobileViewMoreBtn } = this.elements;
      if (!mobileViewMoreBtn) return;

      mobileViewMoreBtn.style.display = this.state.mobileExpanded
        ? "none"
        : "flex";
    }

    bindResizeEvent() {
      const debouncedResize = MilestoneUtils.debounce(() => {
        this.handleResize();
      }, 150);

      window.addEventListener("resize", debouncedResize);
    }

    handleResize() {
      this.generateTimeline();
      this.bindLabelEvents();
      this.bindTickEvents();
      this.updateSlider(this.state.currentIndex);
      this.handleResponsive();

      if (MilestoneUtils.isMobile()) {
        setTimeout(() => this.generateMobileTicks(), 50);
      }
    }

    handleResponsive() {
      const { cardsContainer, timelineContainer, mobileTimeline } =
        this.elements;
      const isMobile = MilestoneUtils.isMobile();

      if (isMobile) {
        if (mobileTimeline) mobileTimeline.style.display = "block";
        if (cardsContainer) cardsContainer.style.display = "none";
        if (timelineContainer) timelineContainer.style.display = "none";
        setTimeout(() => this.generateMobileTicks(), 100);
      } else {
        if (mobileTimeline) mobileTimeline.style.display = "none";
        if (cardsContainer) cardsContainer.style.display = "flex";
        if (timelineContainer) timelineContainer.style.display = "block";
        this.bindLabelEvents();
        this.bindTickEvents();
      }
    }
  }

  const initSection = (section) => {
    if (!section?.classList.contains("milestones-section")) return;

    const root = section.querySelector(".milestones");
    if (!root) return;

    const sectionId = root.dataset.sectionId;
    if (!sectionId) return;

    if (section._milestoneTimeline) return;

    section._milestoneTimeline = new MilestoneTimeline(sectionId);
  };

  initSection(document.currentScript?.parentElement);

  document.addEventListener("shopify:section:load", (event) => {
    initSection(event.target);
  });
})();
